import React, { useEffect, useState } from 'react';
import { Col, Row } from 'components';
import { EditableRow } from './EditableRow';
import { EditableCell } from './EditableCell';
import { PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { DeleteIcon } from 'components/common/icons';
import { BrandItem, BrandItemsTableProps } from './types';
import { useNotificationApi } from 'hooks/useNotificationApi';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { Button, HasAccess, Table, Typography, Popconfirm } from 'components';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { useUpdateLeadItemMutation, useAddLeadItemMutation, useRemoveLeadBrandItemMutation } from 'api';
import styles from './styles.module.scss';

const { Text } = Typography;

export const BrandItemsTable: React.FC<BrandItemsTableProps> = ({
  leadId,
  brandId,
  initialItems,
  isFinalStatus,
  rejected,
}) => {
  const [dataSource, setDataSource] = useState<BrandItem[]>([]);
  const [count, setCount] = useState(0);
  const { handleSuccess, handleError } = useNotificationMessages();
  const notificationApi = useNotificationApi();
  const { hasAccess } = useAccessControl();
  const [editingRow, setEditingRow] = useState<string>('');
  const [editingColumn, setEditingColumn] = useState<string>('');

  const [updateLeadItem] = useUpdateLeadItemMutation();
  const [addLeadItem] = useAddLeadItemMutation();
  const [removeLeadBrandItem, { isLoading: removeLoading }] = useRemoveLeadBrandItemMutation();

  // Determine if the table should be in view-only mode
  const isViewOnly = isFinalStatus || rejected || !hasAccess(PERMISSIONS.leads.lead.write);

  // Convert initialItems to the expected format and set as dataSource
  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      const formattedItems = initialItems.map((item: any, index: number) => ({
        ...item,
        key: item.id || `${index}`,
      }));
      setDataSource(formattedItems);
      setCount(initialItems.length);
    }
  }, [initialItems]);

  const handleDelete = async (record: BrandItem) => {
    // If the item has an ID, it exists in the database and should be deleted via API
    if (record.id) {
      try {
        const { data, error } = await removeLeadBrandItem({
          leadId,
          id: record.id,
          brandId: brandId,
        });

        if (data?.success) {
          handleSuccess('Item was successfully removed');
          // Remove from local state after successful API call
          const newData = dataSource.filter((item) => item.id !== record.id);
          setDataSource(newData);
        } else {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    } else {
      // For newly added items that don't have an ID yet, just remove from local state
      const newData = dataSource.filter((item) => item.key !== record.key);
      setDataSource(newData);
    }
  };

  const handleAdd = () => {
    const newItemKey = `new-${count}`;
    const newItem: BrandItem = {
      key: newItemKey,
      itemNumber: '',
      itemName: '',
      description: '',
      quantity: undefined,
      preferredPrice: undefined,
    };
    // Add to the top of the table
    setDataSource([newItem, ...dataSource]);
    setCount(count + 1);

    // Set the newly added row and column to be edited
    setEditingRow(newItemKey);
    setEditingColumn('itemNumber');
  };

  const handleSave = async (row: BrandItem) => {
    try {
      // Check if item number is provided
      if (!row.itemNumber || row.itemNumber.trim() === '') {
        notificationApi({ type: 'error', message: 'Item Number is required' });
        return;
      }

      // Create payload for the API
      const payload = {
        brandId: brandId,
        leadId: leadId,
        itemNumber: row.itemNumber,
        itemName: row.itemName,
        description: row.description,
        quantity: Number(row.quantity) || undefined,
        preferredPrice: Number(row.preferredPrice) || undefined,
      };

      // Use the appropriate API endpoint based on whether this is a new item or an existing one
      let response;
      if (row.id) {
        // Update existing item
        response = await updateLeadItem({
          ...payload,
          id: row.id,
        });
      } else {
        // Add new item
        response = await addLeadItem(payload);
      }

      const { data, error } = response as any;

      if (data?.success) {
        //handleSuccess(row.id ? 'Item was successfully updated' : 'Item was successfully added');

        // Update the dataSource with the updated data
        const newData = [...dataSource];
        const index = newData.findIndex((item) => row.key === item.key);

        // Replace the item with the updated item, including the new ID from the API response
        if (index > -1) {
          const item = newData[index];
          const updatedItem = {
            ...item,
            ...row,
            id: data.data || item.id, // Use the returned ID or keep the existing one
          };
          newData.splice(index, 1, updatedItem);
          setDataSource(newData);
        }

        // Reset editing state
        setEditingRow('');
        setEditingColumn('');
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const components = {
    body: {
      row: (props: any) => <EditableRow {...props} editingRow={editingRow} editingColumn={editingColumn} />,
      cell: EditableCell,
    },
  };

  const defaultColumns: any[] = [
    {
      title: 'Item Number*',
      dataIndex: 'itemNumber',
      editable: !isViewOnly,
      width: '140px',
      render: (text: string) => (
        <Text
          ellipsis={{
            tooltip: text,
          }}
          style={{ maxWidth: '120px' }}
        >
          {text || '-'}
        </Text>
      ),
    },
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      editable: !isViewOnly,
      width: '140px',
      render: (text: string) => (
        <Text
          ellipsis={{
            tooltip: text,
          }}
          style={{ maxWidth: '120px' }}
        >
          {text || '-'}
        </Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      editable: !isViewOnly,
      width: '240px',
      render: (text: string) => (
        <Text
          ellipsis={{
            tooltip: true,
          }}
          style={{
            maxWidth: '230px',
          }}
        >
          {text || '-'}
        </Text>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      editable: !isViewOnly,
      inputType: 'number',
      width: '120px',
      render: (value: number) => <Text>{value || '-'}</Text>,
    },
    {
      title: 'Preferred Price',
      dataIndex: 'preferredPrice',
      editable: !isViewOnly,
      inputType: 'number',
      width: '120px',
      render: (value: number) => <Text>{value ? `${value}€` : '-'}</Text>,
    },
  ];

  if (!isViewOnly) {
    defaultColumns.push({
      title: '',
      dataIndex: 'operation',
      width: '60px',
      fixed: 'right',
      render: (_: any, record: BrandItem) => (
        <Row justify="center" align="middle">
          <Popconfirm
            title="Are you sure you want to delete this item?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteIcon />} loading={removeLoading} />
          </Popconfirm>
        </Row>
      ),
    });
  }

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: BrandItem) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        inputType: col.inputType,
        handleSave,
      }),
    };
  });

  return (
    <div>
      <Row justify="space-between" align="middle" className={styles.itemsHeaderRow}>
        <Col>
          <Text style={{ fontSize: 18 }} strong>
            Items
          </Text>
        </Col>
        {!isViewOnly && (
          <Col>
            <HasAccess role={PERMISSIONS.leads.lead.write}>
              <Button style={{ color: '#05796B' }} type="link" icon={<PlusOutlined />} onClick={handleAdd}>
                Add Row
              </Button>
            </HasAccess>
          </Col>
        )}
      </Row>
      <div className={styles.tableContainer}>
        <Table
          components={isViewOnly ? {} : components}
          rowClassName={() => (isViewOnly ? '' : 'editable-row')}
          bordered
          dataSource={dataSource}
          columns={columns as any}
          pagination={false}
          size="middle"
          className={styles.table}
          scroll={{ x: 850 }}
        />
      </div>
    </div>
  );
};
